import React from 'react';
import { MagnifyingGlassIcon, ArrowsUpDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { DerivedClientStatus } from './clientStatus';

export type ClientSortField = 'name' | 'date' | 'plan' | 'family';
export type FamilyCountFilter = 'all' | 'none' | 'low' | 'high';

export interface ClientFiltersToolbarProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  planId: string;
  onPlanChange: (id: string) => void;
  planOptions: { id: string; name: string }[];
  gremio: string;
  onGremioChange: (v: string) => void;
  gremioOptions: string[];
  status: DerivedClientStatus | 'all';
  onStatusChange: (v: DerivedClientStatus | 'all') => void;
  family: FamilyCountFilter;
  onFamilyChange: (v: FamilyCountFilter) => void;
  sortField: ClientSortField;
  sortDir: 'asc' | 'desc';
  onSortFieldChange: (f: ClientSortField) => void;
  onSortDirToggle: () => void;
  resultCount: number;
  totalCount: number;
}

const STATUS_OPTIONS: { value: DerivedClientStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'activo', label: 'Activo' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'moroso', label: 'Moroso' },
  { value: 'suspendido', label: 'Suspendido' },
  { value: 'inactivo', label: 'Inactivo' }
];

const FAMILY_OPTIONS: { value: FamilyCountFilter; label: string }[] = [
  { value: 'all', label: 'Familiares (todos)' },
  { value: 'none', label: 'Sin familiares' },
  { value: 'low', label: '1 a 3 familiares' },
  { value: 'high', label: '4 o más' }
];

const SORT_OPTIONS: { value: ClientSortField; label: string }[] = [
  { value: 'name', label: 'Nombre' },
  { value: 'date', label: 'Alta (fecha)' },
  { value: 'plan', label: 'Plan' },
  { value: 'family', label: 'Nº familiares' }
];

const selectCls =
  'h-10 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200';

export const ClientFiltersToolbar: React.FC<ClientFiltersToolbarProps> = ({
  searchTerm,
  onSearchChange,
  planId,
  onPlanChange,
  planOptions,
  gremio,
  onGremioChange,
  gremioOptions,
  status,
  onStatusChange,
  family,
  onFamilyChange,
  sortField,
  sortDir,
  onSortFieldChange,
  onSortDirToggle,
  resultCount,
  totalCount
}) => {
  return (
    <div className="hidden border-b border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white md:block">
      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Buscar por nombre, apellido, cédula, dirección o gremio…"
                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-medium text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                type="search"
                aria-label="Buscar clientes"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <FunnelIcon className="h-4 w-4 text-slate-400" aria-hidden />
              <span>Filtros</span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Plan</span>
                <select
                  className={selectCls}
                  value={planId}
                  onChange={(e) => onPlanChange(e.target.value)}
                >
                  <option value="">Todos los planes</option>
                  {planOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Gremio</span>
                <select
                  className={selectCls}
                  value={gremio}
                  onChange={(e) => onGremioChange(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="__none__">Sin gremio</option>
                  {gremioOptions.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Estado</span>
                <select
                  className={selectCls}
                  value={status}
                  onChange={(e) => onStatusChange(e.target.value as DerivedClientStatus | 'all')}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-slate-600">Familiares</span>
                <select
                  className={selectCls}
                  value={family}
                  onChange={(e) => onFamilyChange(e.target.value as FamilyCountFilter)}
                >
                  {FAMILY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-1 xl:col-span-1">
                <span className="text-xs font-semibold text-slate-600">Orden</span>
                <div className="flex gap-2">
                  <select
                    className={clsx(selectCls, 'flex-1')}
                    value={sortField}
                    onChange={(e) => onSortFieldChange(e.target.value as ClientSortField)}
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={onSortDirToggle}
                    className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    title={sortDir === 'asc' ? 'Ascendente' : 'Descendente'}
                  >
                    <ArrowsUpDownIcon className="h-5 w-5 text-slate-500" />
                    <span className="ml-1.5 hidden sm:inline">{sortDir === 'asc' ? 'Asc' : 'Desc'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end justify-end gap-1 border-t border-slate-100 pt-3 text-right xl:border-t-0 xl:pt-0">
            <p className="text-sm font-semibold text-slate-800">
              <span className="tabular-nums">{resultCount}</span>
              <span className="font-normal text-slate-500"> de </span>
              <span className="tabular-nums text-slate-600">{totalCount}</span>
            </p>
            <p className="text-xs text-slate-500">Resultados filtrados en el directorio</p>
          </div>
        </div>
      </div>
    </div>
  );
};
